/**
 * Converts a string to Title Case by splitting on a delimiter and capitalizing each word.
 * @param str - The string to convert
 * @param delimiter - The delimiter to split on (default: "-")
 * @returns The title-cased string
 */
function toTitleCase(str: string, delimiter: string = "-"): string {
  return str
    .split(delimiter)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Utility function to extract a page title from a URL path.
 * This is a simplified version that works without React hooks or data fetching.
 * Used as a fallback when pageTitle isn't available from the database.
 *
 * @param url - The URL path (e.g., "/topics/123/sections/456?page=1" or "/admin/analytics-dashboard")
 * @returns The page title if found, or null if not a static page
 */
export function getPageTitleFromUrl(url: string | undefined): string | null {
  if (!url) return null;

  try {
    // Extract pathname from URL (remove query params and hash)
    // Handle both absolute and relative URLs
    let pathname: string;
    if (url.startsWith("http://") || url.startsWith("https://")) {
      const urlObj = new URL(url);
      pathname = urlObj.pathname;
    } else {
      // Relative URL - just extract the pathname part
      const urlParts = url.split("?")[0].split("#")[0];
      pathname = urlParts;
    }

    // Special cases that don't follow the pattern
    const specialCases: Record<string, string> = {
      "/": "Home",
      "/details": "Action Details",
    };

    // Check if it's a special case
    if (specialCases[pathname]) {
      return specialCases[pathname];
    }

    // For dynamic routes with ObjectIds, check first before static paths
    const objectIdPattern = "[a-fA-F0-9]{24}";

    // Route patterns that should return null (handled by database)
    const nullRoutes = [
      new RegExp(`/topics/(${objectIdPattern})/sections/(${objectIdPattern})`),
    ];

    // Check for routes that return null
    for (const pattern of nullRoutes) {
      if (pathname.match(pattern)) {
        return null; // Will be handled by pageTitle from database
      }
    }

    // Route patterns that extract title from the route segment
    const dynamicRoutePattern = new RegExp(`/([^/]+)/(${objectIdPattern})$`);
    const match = pathname.match(dynamicRoutePattern);
    if (match) {
      const routeSegment = match[1];
      return toTitleCase(routeSegment, "-");
    }

    // For standard static paths, generate title from pathname
    // Split pathname and get the last segment (or root)
    const segments = pathname.split("/").filter((s) => s.length > 0);
    if (segments.length === 0) {
      return "Home"; // Root path
    }

    // Get the last segment and convert to title case
    const lastSegment = segments[segments.length - 1];
    return toTitleCase(lastSegment, "-");
  } catch (error) {
    // If URL parsing fails, return null
    return null;
  }
}
